import { Params } from '@angular/router';

/**
 * Shared filter-state plumbing for staff list screens: "remember my filters" and filter state in the
 * URL. Each screen keeps its own form; these helpers only move a flat snapshot of it in and out of
 * browser storage and the query string.
 *
 * Every screen uses them only when its `filters.<screen>` feature switch is on. With the switch off,
 * nothing is read or written, and the screen behaves exactly as before.
 */

export type FilterValue = string | number | boolean | string[] | number[] | null;

/** A flat snapshot of a screen's filters. Absent, null or empty values mean "not filtered". */
export type FilterState = Record<string, FilterValue>;

/** How each filter is read back from the query string, where every value arrives as text. */
export type FilterFieldType = 'string' | 'number' | 'boolean' | 'string[]' | 'number[]';
export type FilterSpec = Record<string, FilterFieldType>;

const REMEMBER_PREFIX = 'filters.remember.';

function storageKey(screenKey: string, userKey: string | number): string {
  return `${REMEMBER_PREFIX}${screenKey}.${userKey}`;
}

function isEmpty(value: FilterValue | undefined): boolean {
  return value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
}

/** Drops the empty entries, so a saved or shared state only carries real filters. */
export function compactFilters(state: FilterState): FilterState {
  const result: FilterState = {};
  Object.keys(state ?? {}).forEach(key => {
    if (!isEmpty(state[key])) {
      result[key] = state[key];
    }
  });
  return result;
}

/**
 * Remembers a screen's filters for one user, across visits. localStorage, so it survives a new tab and
 * a browser restart; keyed by user, so a shared machine does not hand one coordinator's filters to the
 * next.
 */
export function rememberFilters(screenKey: string, userKey: string | number, state: FilterState): void {
  if (!screenKey || userKey === null || userKey === undefined) {
    return;
  }
  const compact = compactFilters(state);
  try {
    // Nothing filtered is the same as nothing remembered.
    if (Object.keys(compact).length === 0) {
      localStorage.removeItem(storageKey(screenKey, userKey));
    } else {
      localStorage.setItem(storageKey(screenKey, userKey), JSON.stringify(compact));
    }
  } catch {
    // Storage can be disabled or full. Remembering filters is a convenience, never a requirement.
  }
}

/** The remembered filters, or null when there are none or storage is unavailable. */
export function readRememberedFilters(screenKey: string, userKey: string | number): FilterState | null {
  if (!screenKey || userKey === null || userKey === undefined) {
    return null;
  }
  try {
    const raw = localStorage.getItem(storageKey(screenKey, userKey));
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as FilterState : null;
  } catch {
    // Unreadable or corrupt entries are treated as "nothing remembered".
    return null;
  }
}

export function forgetFilters(screenKey: string, userKey: string | number): void {
  if (!screenKey || userKey === null || userKey === undefined) {
    return;
  }
  try {
    localStorage.removeItem(storageKey(screenKey, userKey));
  } catch {
    // Ignore - see rememberFilters.
  }
}

/**
 * The query-string form of a filter state. A list becomes a repeated param (`?brand=1&brand=2`), so a
 * value may contain any character, commas included. Empty filters are left out. Pass
 * the result to `router.navigate([], { queryParams, replaceUrl: true })`.
 */
export function filtersToQueryParams(state: FilterState, spec: FilterSpec): Params {
  const params: Params = {};
  const compact = compactFilters(state);
  Object.keys(spec).forEach(key => {
    const value = compact[key];
    if (value === undefined) {
      return;
    }
    params[key] = Array.isArray(value) ? (value as Array<string | number>).map(v => String(v)) : String(value);
  });
  return params;
}

/**
 * Query params that clear every filter in the spec, for use with `queryParamsHandling: 'merge'` so
 * that unrelated params (a tab, a record id) survive.
 */
export function clearedQueryParams(spec: FilterSpec): Params {
  const params: Params = {};
  Object.keys(spec).forEach(key => params[key] = null);
  return params;
}

/**
 * Reads a filter state back from the query string. Only keys in the spec are read, and a value that
 * does not parse as its type is ignored. Returns null when the URL carries none of the filters, so the
 * caller can fall back to remembered or default filters.
 */
export function filtersFromQueryParams(params: Params, spec: FilterSpec): FilterState | null {
  const state: FilterState = {};
  Object.keys(spec).forEach(key => {
    const raw = params?.[key];
    if (raw === null || raw === undefined || raw === '') {
      return;
    }
    const texts: string[] = Array.isArray(raw) ? raw.map(v => String(v)) : [String(raw)];
    const value = parseValue(texts, spec[key]);
    if (!isEmpty(value)) {
      state[key] = value;
    }
  });
  return Object.keys(state).length > 0 ? state : null;
}

function parseValue(texts: string[], type: FilterFieldType): FilterValue {
  const text = texts[0];
  switch (type) {
    case 'number': {
      const n = Number(text);
      return Number.isFinite(n) ? n : null;
    }
    case 'boolean':
      return text === 'true' ? true : text === 'false' ? false : null;
    case 'string[]':
      return texts.filter(v => v !== '');
    case 'number[]':
      return texts.filter(v => v !== '').map(v => Number(v)).filter(n => Number.isFinite(n));
    default:
      return text;
  }
}
